import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus, ArrowLeft, ExternalLink, Play, Pause, Trash2,
  BarChart3, FlaskConical, Link2, Copy, Check, Edit2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STEP_NAMES = {
  1: 'Poop Frequency', 2: 'Symptoms', 3: 'Meet Hashi-aid', 4: 'Support Areas',
  5: 'Whole Body Health', 6: 'Current Supplements', 7: 'Absorption Info',
  8: 'Comparison', 9: 'Clinical Research', 10: 'Timeline', 11: 'Email Capture'
};

const SplitTests = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultLpLink, setDefaultLpLink] = useState('');
  const [editingLp, setEditingLp] = useState(false);
  const [lpInput, setLpInput] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const quizBaseUrl = window.location.origin;

  const fetchTests = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/split-tests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTests(data.tests || []);
    } catch (err) {
      console.error('Failed to fetch tests:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings`);
      const data = await res.json();
      setDefaultLpLink(data.lp_link || '');
      setLpInput(data.lp_link || '');
    } catch (err) {}
  }, []);

  useEffect(() => {
    fetchTests();
    fetchSettings();
  }, [fetchTests, fetchSettings]);

  const saveLpLink = async () => {
    await fetch(`${API_URL}/api/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ lp_link: lpInput })
    });
    setDefaultLpLink(lpInput);
    setEditingLp(false);
  };

  const updateStatus = async (testId, newStatus) => {
    await fetch(`${API_URL}/api/split-tests/${testId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status: newStatus })
    });
    fetchTests();
  };

  const deleteTest = async (testId) => {
    if (!window.confirm('Delete this split test? This cannot be undone.')) return;
    await fetch(`${API_URL}/api/split-tests/${testId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    fetchTests();
  };

  const copyUrl = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getQuizUrl = (slug) => slug ? `${quizBaseUrl}/${slug}` : `${quizBaseUrl}/`;

  const statusStyles = {
    draft: { bg: 'rgba(107, 114, 128, 0.15)', text: '#9ca3af', label: 'Draft' },
    active: { bg: 'rgba(16, 185, 129, 0.15)', text: '#34d399', label: 'Active' },
    paused: { bg: 'rgba(245, 158, 11, 0.15)', text: '#fbbf24', label: 'Paused' },
    completed: { bg: 'rgba(59, 130, 246, 0.15)', text: '#60a5fa', label: 'Completed' }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0a0f' }}>
        <div className="w-12 h-12 rounded-full animate-pulse" style={{ background: 'linear-gradient(135deg, #7874F8, #09C9E9)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0f' }}>
      {/* Header */}
      <header className="sticky top-0 z-50 border-b" style={{ background: '#0f0f17', borderColor: '#1e1e2e' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-gray-500 hover:text-gray-300 transition" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-purple-400" />
              Split Tests
            </h1>
          </div>
          <Link
            to="/admin/split-tests/new"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium transition hover:shadow-lg"
            style={{ background: 'linear-gradient(135deg, #7874F8, #09C9E9)' }}
            data-testid="create-test-btn"
          >
            <Plus className="w-4 h-4" /> New Test
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Default LP Link */}
        <div className="rounded-xl p-5 mb-8" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link2 className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-sm font-medium text-gray-300">Default Landing Page Link</p>
                <p className="text-xs text-gray-600">Where users go after completing the default quiz</p>
              </div>
            </div>
            {editingLp ? (
              <div className="flex items-center gap-2">
                <input
                  data-testid="default-lp-input"
                  value={lpInput}
                  onChange={(e) => setLpInput(e.target.value)}
                  className="px-3 py-1.5 rounded-lg text-sm text-gray-200"
                  style={{ background: '#1a1a26', border: '1px solid #2a2a3a', width: '320px' }}
                  placeholder="https://yoursite.com/offer"
                />
                <button onClick={saveLpLink} className="px-3 py-1.5 rounded-lg text-sm text-white" style={{ background: '#7874F8' }}>Save</button>
                <button onClick={() => { setEditingLp(false); setLpInput(defaultLpLink); }} className="px-3 py-1.5 rounded-lg text-sm text-gray-400 hover:text-gray-200">Cancel</button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {defaultLpLink ? (
                  <a href={defaultLpLink} target="_blank" rel="noreferrer" className="text-sm text-cyan-400 hover:underline flex items-center gap-1">
                    {defaultLpLink} <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="text-sm text-gray-600">Not set</span>
                )}
                <button onClick={() => setEditingLp(true)} className="text-gray-500 hover:text-gray-300 ml-2" data-testid="edit-lp-btn">
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Default Quiz URL */}
        <div className="rounded-xl p-5 mb-8" style={{ background: '#13131a', border: '1px solid #1e1e2e' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              <div>
                <p className="text-sm font-medium text-gray-300">Default Quiz URL</p>
                <p className="text-xs text-gray-600">Main quiz with no split test modifications</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <code className="text-sm text-cyan-400 px-3 py-1 rounded" style={{ background: '#1a1a26' }}>{quizBaseUrl}/</code>
              <button onClick={() => copyUrl(quizBaseUrl + '/', 'default')} className="text-gray-500 hover:text-gray-300">
                {copiedId === 'default' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Tests Grid */}
        {tests.length === 0 ? (
          <div className="text-center py-16">
            <FlaskConical className="w-12 h-12 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No split tests yet</p>
            <p className="text-sm text-gray-600">Create your first test to start optimizing quiz performance</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {tests.map((test) => {
              const status = statusStyles[test.status] || statusStyles.draft;
              const overrideSteps = Object.keys(test.step_overrides || {});
              const url = getQuizUrl(test.slug);

              return (
                <div key={test.id} className="rounded-xl p-5" style={{ background: '#13131a', border: '1px solid #1e1e2e' }} data-testid={`test-card-${test.id}`}>
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-base font-semibold text-gray-200">{test.name}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full mt-1 inline-block" style={{ background: status.bg, color: status.text }}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      {test.status === 'draft' || test.status === 'paused' ? (
                        <button onClick={() => updateStatus(test.id, 'active')} className="p-1.5 rounded-lg hover:bg-white/5" title="Activate" data-testid={`activate-${test.id}`}>
                          <Play className="w-4 h-4 text-green-400" />
                        </button>
                      ) : test.status === 'active' ? (
                        <button onClick={() => updateStatus(test.id, 'paused')} className="p-1.5 rounded-lg hover:bg-white/5" title="Pause" data-testid={`pause-${test.id}`}>
                          <Pause className="w-4 h-4 text-yellow-400" />
                        </button>
                      ) : null}
                      <Link to={`/admin/split-tests/${test.id}/edit`} className="p-1.5 rounded-lg hover:bg-white/5" title="Edit">
                        <Edit2 className="w-4 h-4 text-gray-400" />
                      </Link>
                      <button onClick={() => deleteTest(test.id)} className="p-1.5 rounded-lg hover:bg-white/5" title="Delete" data-testid={`delete-${test.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Quiz URL</span>
                      <div className="flex items-center gap-1">
                        <code className="text-cyan-400 text-xs px-2 py-0.5 rounded" style={{ background: '#1a1a26' }}>{url}</code>
                        <button onClick={() => copyUrl(url, test.id)} className="text-gray-600 hover:text-gray-400">
                          {copiedId === test.id ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">LP Link</span>
                      {test.lp_link ? (
                        <a href={test.lp_link} target="_blank" rel="noreferrer" className="text-cyan-400 text-xs hover:underline flex items-center gap-1">
                          {test.lp_link.length > 40 ? test.lp_link.substring(0, 40) + '...' : test.lp_link}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-gray-600 text-xs">Uses default</span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Ratio</span>
                      <span className="text-gray-300">{test.ratio?.[0]}% control / {test.ratio?.[1]}% variant</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Steps Modified</span>
                      <div className="flex gap-1">
                        {overrideSteps.length > 0 ? overrideSteps.map(s => (
                          <span key={s} className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#7874F815', color: '#a5a2ff' }}>
                            {s}
                          </span>
                        )) : <span className="text-gray-600 text-xs">None</span>}
                      </div>
                    </div>
                  </div>

                  {/* View Results */}
                  <Link
                    to={`/admin/split-tests/${test.id}`}
                    className="flex items-center justify-center gap-2 w-full py-2 rounded-lg text-sm font-medium text-purple-300 transition hover:bg-white/5"
                    style={{ border: '1px solid #2a2a3a' }}
                    data-testid={`results-${test.id}`}
                  >
                    <BarChart3 className="w-4 h-4" /> View Results
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default SplitTests;
